import { Refine, Authenticated } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { ClerkProvider, useAuth, useUser } from "@clerk/react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";

import { Layout } from "./components/Layout";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ErrorPage } from "./pages/ErrorPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthErrorPage } from "./pages/AuthErrorPage";
import { LoginPage } from "./pages/LoginPage";
import { FeedPage } from "./pages/FeedPage";
import { GridPage } from "./pages/GridPage";
import { HistoryPage } from "./pages/HistoryPage";
import { ComparisonPage } from "./pages/ComparisonPage";
import { CatalogPage } from "./pages/CatalogPage";
import { ProfilePage } from "./pages/ProfilePage";
import { ProductsPage } from "./pages/admin/ProductsPage";
import { ProductDetailPage } from "./pages/admin/ProductDetailPage";
import { BrandsPage } from "./pages/admin/BrandsPage";
import { RefreshReviewPage } from "./pages/admin/RefreshReviewPage";
import { RetailersPage } from "./pages/admin/RetailersPage";
import { CategoriesPage } from "./pages/admin/CategoriesPage";
import { ClassifyPage } from "./pages/admin/ClassifyPage";
import { ScrapersPage } from "./pages/admin/ScrapersPage";
import { ReviewQueuePage } from "./pages/admin/ReviewQueuePage";
import { UsersPage } from "./pages/admin/UsersPage";
import { DiscoverPage } from "./pages/admin/DiscoverPage";
import dataProvider from "./providers/dataProvider";
import { createAuthProvider } from "./providers/authProvider";
import { clerkAppearance } from "./lib/clerk-appearance";
import { theme } from "./theme";

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

function ProtectedPageRoutes() {
  return (
    <Routes>
      <Route path="login/*" element={<LoginPage />} />
      <Route
        element={
          <Authenticated key="auth" fallback={<Navigate to="/login" />} loading={<div />}>
            <Layout><Outlet /></Layout>
          </Authenticated>
        }
      >
        <Route index element={<FeedPage />} />
        <Route path="grid" element={<GridPage />} />
        <Route path="history/:productId?" element={<HistoryPage />} />
        <Route path="comparison" element={<ComparisonPage />} />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="admin/discover" element={<DiscoverPage />} />
        <Route path="admin/products" element={<ProductsPage />} />
        <Route path="admin/products/:id" element={<ProductDetailPage />} />
        <Route path="admin/brands" element={<BrandsPage />} />
        <Route path="admin/brands/:brandId/refresh-review" element={<RefreshReviewPage />} />
        <Route path="admin/retailers" element={<RetailersPage />} />
        <Route path="admin/categories" element={<CategoriesPage />} />
        <Route path="admin/classify" element={<ClassifyPage />} />
        <Route path="admin/scrapers" element={<ScrapersPage />} />
        <Route path="admin/review-queue" element={<ReviewQueuePage />} />
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="profile/*" element={<ProfilePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function AuthenticatedApp() {
  const { pathname } = useLocation();
  const auth = useAuth();
  const userCtx = useUser();
  const authProvider = createAuthProvider(() => auth, () => userCtx);

  // Clerk-level gate: redirect to /login before Refine even mounts protected routes.
  // Refine's <Authenticated> check() only fires on mount/route-change — it misses
  // the initial Clerk load transition from undefined → false. This gate catches it.
  if (auth.isLoaded && !auth.isSignedIn && !pathname.startsWith("/login")) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Refine
      routerProvider={routerProvider}
      dataProvider={dataProvider}
      authProvider={authProvider}
      resources={[
        { name: "products", list: "/admin/products" },
        { name: "brands", list: "/admin/brands" },
        { name: "retailers", list: "/admin/retailers" },
        { name: "categories", list: "/admin/categories" },
        { name: "classify", list: "/admin/classify" },
      ]}
      options={{ syncWithLocation: true }}
    >
      <ErrorBoundary fallback={<ErrorPage />} resetKeys={[pathname]}>
        <ProtectedPageRoutes />
      </ErrorBoundary>
    </Refine>
  );
}

function ClerkProviderWithRouting({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  return (
    <ClerkProvider
      publishableKey={CLERK_KEY!}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      afterSignOutUrl="/login"
      appearance={clerkAppearance}
    >
      {children}
    </ClerkProvider>
  );
}

function App() {
  if (!CLERK_KEY) {
    console.error(
      "[PriceWatch] VITE_CLERK_PUBLISHABLE_KEY is not configured. Authentication will not work.",
    );
    return (
      <BrowserRouter>
        <MantineProvider theme={theme} defaultColorScheme="light">
          <AuthErrorPage />
        </MantineProvider>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <ModalsProvider>
          <Notifications position="top-right" />
          <ClerkProviderWithRouting>
            <AuthenticatedApp />
          </ClerkProviderWithRouting>
        </ModalsProvider>
      </MantineProvider>
      <Analytics />
      <SpeedInsights />
    </BrowserRouter>
  );
}

export default App;
